import { useCallback, useEffect, useMemo } from 'react'

import { FixedBanner } from '@circlefin/components/lib/FixedBanner'
import { TypeGuards } from '@services/type-guards'
import { useConnectWallet } from '@shared/wallet/hooks/useConnectWallet'
import useTranslation from 'next-translate/useTranslation'

import { ConnectWalletButton, WrongNetworkAlert } from '../../components'
import { useAceBlockchain } from '../../hooks'

import type { BlockchainAbbreviation } from '@shared/graphql'

export interface ConnectRecipientWalletProps {
  /**
   * Destination address.
   */
  address: string
  /**
   * Destination chain.
   */
  chain: BlockchainAbbreviation
  /**
   * Connected wallet label.
   */
  label?: string
  /**
   * On Success Callback.
   */
  onSuccess?: () => void
  /**
   * On Error Callback.
   */
  onError?: () => void
}

export const ConnectRecipientWallet: React.FC<ConnectRecipientWalletProps> = ({
  address,
  chain,
  label,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation('ace')

  const {
    address: connectedWalletAddress,
    activeChain: connectedWalletChain,
    isConnected,
    isConnecting,
    switchChain,
  } = useConnectWallet()

  const { aceBlockchain } = useAceBlockchain(chain)

  const isWalletMismatch = useMemo(
    () =>
      isConnected &&
      connectedWalletAddress.toLowerCase() !== address.toLowerCase(),
    [address, connectedWalletAddress, isConnected],
  )
  const isWrongNetwork = useMemo(
    () => isConnected && connectedWalletChain?.id !== aceBlockchain?.chainId,
    [aceBlockchain?.chainId, connectedWalletChain?.id, isConnected],
  )

  const handleSwitchNetwork = useCallback(() => {
    if (
      TypeGuards.AcceleratedChainExpansion.isSupportedChainId(
        aceBlockchain?.chainId,
      )
    ) {
      void switchChain(aceBlockchain.chainId)
    }
  }, [aceBlockchain?.chainId, switchChain])

  useEffect(() => {
    if (isWalletMismatch || isWrongNetwork) {
      onError?.()
    } else if (isConnected) {
      onSuccess?.()
    }
  }, [isConnected, isWalletMismatch, isWrongNetwork, onError, onSuccess])

  return (
    <div>
      {isConnected && label != null ? (
        <div
          className="pb-2 pt-4 type-h-title-md"
          data-testid="connected-wallet-label"
        >
          {label}
        </div>
      ) : null}
      <ConnectWalletButton />

      {/* Wallet address error */}
      {isWalletMismatch ? (
        <FixedBanner className="mt-4" multiline={false} status="error">
          <FixedBanner.Description>
            {t('alert.walletMismatch.description', {
              address: address.slice(-6),
            })}
          </FixedBanner.Description>
        </FixedBanner>
      ) : null}

      {/* Network error */}
      {!isWalletMismatch && isWrongNetwork ? (
        <WrongNetworkAlert
          blockchain={chain}
          className="mt-4"
          isConnecting={isConnecting}
          onSwitchNetwork={handleSwitchNetwork}
        />
      ) : null}
    </div>
  )
}
